import { doc, getDoc, onSnapshot, type DocumentData, type Unsubscribe } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import type {
  AiAnalysisSettings,
  AiCategoryMaster,
  AiCategoryMasterRow,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * AI 解析結果の chip 表示用 M2 master 解決 hook (Sprint X2、旧 `useCategoryMasters` L3 踏襲)。
 *
 * 流れ (旧 v1 と同じ):
 *   1. settingsId から M1 `aiAnalysisSettings/{settingsId}` を読み込む
 *   2. settings.categoryMasterIds[] (or 後方互換 categoryMasterId) を M2 doc ID として取得
 *   3. 各 M2 `aiCategoryMasters/{id}` を fetch、rows[] を中心とした lookup map を構築
 *
 * 戻り値:
 *   - byCategoryCd: rows[].categoryCd → row (codeField 由来の chip 解決用)
 *   - byCategoryLabel: rows[].category → row (emotion label 由来の chip 解決用)
 *   - byTemplateVar: 'emotionMaster' / 'categoryMaster' 別の master direct lookup
 *   - colorOf(row): M2.colorAxis + colorMap (or row.color) から chip 色を決定
 *
 * 旧との差分:
 *   - 旧 `loadCategoryMaster` 内部 helper を統合し getDoc 直叩きに narrow
 *   - module-level cache (Map) で重複 fetch 抑制 (旧 _settingsCache / loadCategoryMaster cache を踏襲)
 *   - PostDetailModal が同一 post 開閉で settingsId が固定 → cache hit 率高い
 *
 * 制約 (R-34 知見):
 *   - getDoc は onSnapshot より軽量で再 mount 多発しないため `setState in effect` 警告対象外
 *   - master 自体が頻繁に変わるユースケースは想定外 (admin 編集後は user reload で OK)
 */

export interface AiCategoryMastersResult {
  /** rows[].categoryCd → row (例: 'A01' → 広場・公園 row)。 */
  byCategoryCd: Map<string, AiCategoryMasterRow>;
  /** rows[].category → row (例: '喜び' → row、emotion label 用)。 */
  byCategoryLabel: Map<string, AiCategoryMasterRow>;
  /** master.templateVar → master 全体 (例: 'emotionMaster' → ecw-emotion master)。 */
  byTemplateVar: Map<string, AiCategoryMaster>;
  /** 読み込んだ M2 master の配列。 */
  masters: AiCategoryMaster[];
  loading: boolean;
}

// module-level cache (settingsId / masterId 別)
const _settingsCache = new Map<string, AiAnalysisSettings | null>();
const _masterCache = new Map<string, AiCategoryMaster | null>();

async function loadSettings(settingsId: string): Promise<AiAnalysisSettings | null> {
  if (_settingsCache.has(settingsId)) return _settingsCache.get(settingsId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiAnalysisSettings', settingsId));
    if (!snap.exists()) {
      _settingsCache.set(settingsId, null);
      return null;
    }
    const data = { ...(snap.data() as DocumentData), id: settingsId } as AiAnalysisSettings;
    _settingsCache.set(settingsId, data);
    return data;
  } catch {
    _settingsCache.set(settingsId, null);
    return null;
  }
}

async function loadMaster(masterId: string): Promise<AiCategoryMaster | null> {
  if (_masterCache.has(masterId)) return _masterCache.get(masterId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiCategoryMasters', masterId));
    if (!snap.exists()) {
      _masterCache.set(masterId, null);
      return null;
    }
    const data = { ...(snap.data() as DocumentData), id: masterId } as AiCategoryMaster;
    _masterCache.set(masterId, data);
    return data;
  } catch {
    _masterCache.set(masterId, null);
    return null;
  }
}

const EMPTY_MAP_RESULT: AiCategoryMastersResult = {
  byCategoryCd: new Map(),
  byCategoryLabel: new Map(),
  byTemplateVar: new Map(),
  masters: [],
  loading: false,
};

/** 解析設定 (M1) が参照するカテゴリマスタを読む (hook の外から使う版、docs/29 分析アプリの観点)。 */
export async function loadCategoryMastersForSettings(
  settingsId: string,
): Promise<AiCategoryMaster[]> {
  const settings = await loadSettings(settingsId);
  const ids = (
    settings?.categoryMasterIds?.length
      ? settings.categoryMasterIds
      : settings?.categoryMasterId
        ? [settings.categoryMasterId]
        : []
  ) as string[];
  return (await Promise.all(ids.map(loadMaster))).filter((m): m is AiCategoryMaster => !!m);
}

/** 読み込んだマスタから引き当て表を作る (pure)。 */
export function buildCategoryMastersResult(
  masters: AiCategoryMaster[],
  loading = false,
): AiCategoryMastersResult {
  const byCategoryCd = new Map<string, AiCategoryMasterRow>();
  const byCategoryLabel = new Map<string, AiCategoryMasterRow>();
  const byTemplateVar = new Map<string, AiCategoryMaster>();
  for (const m of masters) {
    if (m.templateVar) byTemplateVar.set(m.templateVar, m);
    for (const r of m.rows ?? []) {
      if (r.categoryCd && !byCategoryCd.has(r.categoryCd)) byCategoryCd.set(r.categoryCd, r);
      // AI 出力/保存は「大分類CD-カテゴリCD」結合形 (例 "D-01")。bare categoryCd ("01") のみだと
      // 結合コードで引けず日本語名が解決されない (コードのまま表示される) ため、結合キーも登録する。
      if (r.majorCd && r.categoryCd) {
        const combined = `${r.majorCd}-${r.categoryCd}`;
        if (!byCategoryCd.has(combined)) byCategoryCd.set(combined, r);
      }
      if (r.category && !byCategoryLabel.has(r.category)) byCategoryLabel.set(r.category, r);
    }
  }
  return { byCategoryCd, byCategoryLabel, byTemplateVar, masters, loading };
}

export function useAiCategoryMasters(
  settingsId: string | null | undefined,
): AiCategoryMastersResult {
  const [masters, setMasters] = useState<AiCategoryMaster[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!settingsId) {
      setMasters([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const settings = await loadSettings(settingsId);
      if (cancelled) return;
      const ids = (
        settings?.categoryMasterIds?.length
          ? settings.categoryMasterIds
          : settings?.categoryMasterId
            ? [settings.categoryMasterId]
            : []
      ) as string[];
      const loaded = (await Promise.all(ids.map(loadMaster))).filter(
        (m): m is AiCategoryMaster => !!m,
      );
      if (!cancelled) {
        setMasters(loaded);
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [settingsId]);

  return useMemo(() => {
    if (!settingsId) return EMPTY_MAP_RESULT;
    return buildCategoryMastersResult(masters, loading);
  }, [settingsId, masters, loading]);
}

/**
 * 行 + 所属 master から chip の表示色を決定 (旧 `formatCategoryLabel` + colorAxis 解決の踏襲)。
 *
 *   - master.colorAxis === 'category' / 'categoryCd': row.color (行直結) を優先し、
 *     未設定なら colorMap[row.category] / colorMap[row.categoryCd] を second source として見る
 *   - master.colorAxis === 'major' / 'majorCd': colorMap[row.major or row.majorCd] を参照
 *   - fallback: '#94A3B8' (slate-400)
 *
 * category 軸で colorMap も見るのは、感情マスタ (`ecw-emotion`) のように
 * 「colorMap にカテゴリ名 → 色を持つが rows[].color は空」という既存データがあるため。
 * これを見ないと全チップが slate-400 に潰れて色分けが消える (色設定が効かない)。
 */
export function resolveChipColor(
  row: AiCategoryMasterRow,
  master: AiCategoryMaster | undefined,
): string {
  const fallback = '#94A3B8';
  if (!master) return row.color ?? fallback;
  const axis = master.colorAxis;
  if (axis === 'category' || axis === 'categoryCd') {
    if (row.color) return row.color;
    const key = axis === 'category' ? row.category : row.categoryCd;
    if (key && master.colorMap?.[key]) return master.colorMap[key];
    return fallback;
  }
  const key = axis === 'major' ? row.major : row.majorCd;
  if (key && master.colorMap?.[key]) return master.colorMap[key];
  return row.color ?? fallback;
}

/** 別 hook ユーザー (Phase 5 後続 / analysis hook) 向けに onSnapshot 版も提供 (Sprint X3 で使用予定)。 */
export function useAiCategoryMasterById(masterId: string | null): {
  master: AiCategoryMaster | null;
  loading: boolean;
} {
  const [master, setMaster] = useState<AiCategoryMaster | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!masterId) {
      setMaster(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub: Unsubscribe = onSnapshot(
      doc(db, 'aiCategoryMasters', masterId),
      (snap) => {
        if (!snap.exists()) {
          setMaster(null);
        } else {
          setMaster({ ...(snap.data() as DocumentData), id: masterId } as AiCategoryMaster);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [masterId]);
  return { master, loading };
}
